import apiCore from "../../utils/api-core.js";

export const getInboxFilters = (companyId) => {
  return apiCore.get(`/api/v3/inbox/filters/${companyId}`);
};

export const getInboxTags = (companyId) => {
  return apiCore.get(`/api/v3/inbox/tags/${companyId}`);
};

export const getInboxCampaigns = (companyId) => {
  return apiCore.get(`/api/v3/inbox/campaigns/${companyId}`);
};

export const getContactModel = (contactId) => {
  return apiCore.get(`/api/v3/inbox/contact/${contactId}`);
};

export const deleteContactTag = (tagId) => {
  return apiCore.delete(`/api/v3/inbox/contact/tag/${tagId}`);
};

export const addContactTag = (contactId, value) => {
  return apiCore.post(`/api/v3/inbox/contact/tag/${contactId}`, { value });
};

export const setContactStar = (contactId, value) => {
  return apiCore.put(`/api/v3/inbox/contact/star/${contactId}`, { value });
};

export const getContactThreads = (contactId, query) => {
  return apiCore.get(`/api/v3/inbox/${contactId}/threads?${query}`);
};

export const getInboxConversationsList = (
  filters,
  companyId,
  page,
  pageSize
) => {
  return apiCore.post(
    `/api/v3/inbox/conversations/${companyId}/list?page=${page}&pageSize=${pageSize}`,
    filters
  );
};

export const getUserMessageTemplates = () => {
  return apiCore.get(`/api/v1/templates`);
};

export const checkUserGroupDefault = (group) => {
  return apiCore.get(`/api/v1/apps/check-default/${group}`);
};

export const sendInterviewScheduling = (body) => {
  return apiCore.post("/api/v1/conversation/send/scheduling", body);
};

export const downloadContact = (contactId) => {
  return apiCore.get(`/api/v3/contacts/${contactId}/pdf`);
};

export function deleteScheduledMessage(id) {
  return apiCore.delete(`/api/v1/scheduled/${id}`);
}

export function cancelScheduledMessage(id) {
  return apiCore.post(`/api/v3/scheduled-messages/${id}/cancel`);
}

export function sendScheduledMessageNow(id) {
  return apiCore.post(`/api/v3/scheduled-messages/${id}/send-now`);
}

export function muteContactEventReminders(contactId, muted) {
  return apiCore.put(`/api/v3/contacts/${contactId}/mute-event-reminders`, { muted });
}

export function sendReply(body) {
  return apiCore.post("/api/v1/conversation/send", body);
}

export const shortenUrl = (payload) => {
  return apiCore.post('/api/v1/shortener', payload);
};

export function readContactNotifications(contactId){
    return apiCore.put(`/api/v1/notifications/contact/is-read/${contactId}`)
}

export const getSequenceTemplates = () => {
    return apiCore.get('/api/v3/module-group', {
        params: { scope: 'owned', limit: 100, sort: 'created_at', order: 'desc' }
    });
};

export const getSequencePreview = (moduleGroupId) => {
    return apiCore.get(`/api/v3/module-group/${moduleGroupId}/preview`);
};

export const enrollContactInSequence = (moduleGroupId, contactId, conversationIdOrBotmap) => {
    // Accept either { conversation_id } or { botmap_id } so callers don't need a fully-loaded
    // contact model. Backend resolves/creates the conversation from botmap_id when needed.
    const payload = conversationIdOrBotmap && conversationIdOrBotmap.botmap_id
        ? { contact_id: contactId, botmap_id: conversationIdOrBotmap.botmap_id }
        : { contact_id: contactId, conversation_id: conversationIdOrBotmap };
    return apiCore.post(`/api/v3/module-group/${moduleGroupId}/enroll`, payload);
};

export const getContactActivity = (contactId, params = {}) => {
    return apiCore.get(`/api/v3/contacts/${contactId}/activity`, { params });
};

export const getCompanyGroups = (companyId, contactId?) => {
    const query = contactId ? `?contactId=${contactId}` : '';
    return apiCore.get(`/api/v3/groups/company/${companyId}${query}`);
};

export const addContactToGroups = (contactId, groupIds) => {
    return apiCore.post(`/api/v3/contact/${contactId}/groups`, { groupIds });
};

export const removeContactFromGroups = (contactId, groupIds) => {
    return apiCore.delete(`/api/v3/contact/${contactId}/groups`, { groupIds });
};

export const createGroup = (name, contacts = []) => {
    return apiCore.post('/api/v3/groups', { name, privacy: 'public', contacts });
};

// Inbox multi-select → "Add tag": one value applied to many contacts in a
// single request. Response data: { added, skipped, invalid, addedContactIds }.
export const bulkAddContactTags = (contactIds: string[], value: string) => {
    return apiCore.post('/api/v3/contacts/tags/bulk', { contactIds, value });
};

// Per-user inbox Archive (ADR-034): archive or unarchive one or many
// conversations for the calling user. Response data: { updated, invalid }.
export const setContactsArchived = (contactIds: string[], archived: boolean) => {
    return apiCore.post('/api/v3/inbox/archive', { contactIds, archived });
};

// Inbox multi-select → "Add to group": many contacts into one existing group.
export const addGroupContacts = (groupId: string, contactIds: string[]) => {
    return apiCore.post(`/api/v3/groups/${groupId}/contacts`, { contacts: contactIds });
};

// ── AI translation (ADR-035) ─────────────────────────────────────────────
// Composer preview. Data: { translated, source_lang, target_lang, same_language, outcome }.
// Rejects with the backend payload: code TRANSLATION_DISABLED | TRANSLATION_CAPPED (403),
// TRANSLATION_FAILED (502).
export const translateText = (payload: {
    text: string; target_lang: string; source_lang?: string | null; contactId?: string; conversationId?: string;
}) => {
    return apiCore.post('/api/v3/ai/translate', payload);
};

// On-demand translation of one stored message (history / other viewer languages).
// Data: { thread_id, target, outcome, translation }.
export const translateThread = (threadId: string, payload: { target_lang?: string; force?: boolean } = {}) => {
    return apiCore.post(`/api/v3/inbox/threads/${threadId}/translate`, payload);
};

// Contact language — '' clears it. Server marks a value set here as `manual`.
export const updateContactLanguage = (contactId: string, language: string | null) => {
    return apiCore.put(`/api/v3/contact/${contactId}/update-details`, { language: language || '' });
};
